import { z } from "zod";

import type { ModuleID, VersionString } from "@bestowinc/tinkertoy-core";

import {
  BundleImportError,
  importBundle,
  importBundleModule,
} from "../bundle-loader/import-bundle";
import { trackModuleBundleLoad } from "../performance-telemetry/bundle-load-tracking";

import type {
  LoadedModule,
  LoadModuleOptions,
  LoadModuleResult,
  ModuleLoader,
  ModuleLoaderConfig,
  ModuleLoadError,
} from "./types";

/**
 * Build the CDN URL for a module bundle.
 *
 * Pattern: `{cdnBaseUrl}/modules/{moduleId}/shell-{shellVersion}/{version}.js`
 */
export function resolveCdnUrl(
  moduleId: ModuleID,
  version: VersionString,
  config: ModuleLoaderConfig,
): string {
  return `${config.cdnBaseUrl}/modules/${moduleId}/shell-${config.shellVersion}/${version}.js`;
}

// `.passthrough()` is required: the loader validates the fields it depends on
// (`id`/`type`/`component`) but returns the WHOLE definition downstream (see the
// `as LoadedModule["definition"]` cast below). A bare `z.object` strips unknown
// keys, which would drop `actions` (defaulted to `{}` by `defineModule`) and
// `dataFetcher` — and `createClientActionMap` then crashes on
// `Object.entries(undefined)`. Passthrough keeps the full object intact.
const moduleDefinitionSchema = z
  .object({
    id: z.string(),
    type: z.enum(["leaf", "branch", "layout", "error"]),
    component: z.function(),
  })
  .passthrough();

function makeLoadError(
  moduleId: ModuleID,
  version: VersionString,
  cdnUrl: string,
  variant:
    | { code: "NETWORK_ERROR"; cause: Error }
    | { code: "INVALID_MODULE"; reason: string }
    | { code: "IMPORT_ERROR"; cause: Error },
): ModuleLoadError {
  return { moduleId, version, cdnUrl, ...variant };
}

/**
 * Create a stateless module loader bound to a CDN base URL and shell version.
 *
 * Caching and deduplication are handled by the browser's ES module map —
 * once a URL is imported, the runtime returns the same module namespace
 * on subsequent `import()` calls without re-fetching or re-evaluating.
 *
 * @example
 * ```ts
 * import { SHELL_VERSION } from "@bestowinc/tinkertoy-shell-deps";
 *
 * const loader = createModuleLoader({
 *   cdnBaseUrl: "https://cdn.example.com",
 *   shellVersion: SHELL_VERSION,
 * });
 *
 * const { definition } = await loader.loadModule("registration/form", "1.1.0");
 * ```
 */
export function createModuleLoader(config: ModuleLoaderConfig): ModuleLoader {
  const importFn = config.__importFn ?? importBundleModule;

  async function loadModule(
    moduleId: ModuleID,
    version: VersionString,
    options?: LoadModuleOptions,
  ): Promise<LoadedModule> {
    const cacheMode =
      options?.cacheMode ??
      (config.bundleOverrides?.[moduleId] ? "dev_override" : "cold");

    return trackModuleBundleLoad(
      { moduleId, version, cacheMode },
      async () => loadModuleInner(moduleId, version, options),
    );
  }

  async function loadModuleInner(
    moduleId: ModuleID,
    version: VersionString,
    options?: LoadModuleOptions,
  ): Promise<LoadedModule> {
    // A dev override (from the `ttv2-dev-override` cookie) points the browser at
    // a locally-served bundle for this module; otherwise resolve the CDN URL.
    const cdnUrl =
      config.bundleOverrides?.[moduleId] ??
      resolveCdnUrl(moduleId, version, config);

    let namespace: Record<string, unknown>;
    try {
      namespace = await importBundle(cdnUrl, importFn);
    } catch (err) {
      if (err instanceof BundleImportError) {
        const cause =
          err.cause instanceof Error ? err.cause : new Error(String(err.cause));
        throw makeLoadError(moduleId, version, cdnUrl, {
          code: err.code,
          cause,
        });
      }
      throw err;
    }

    const parsed = moduleDefinitionSchema.safeParse(namespace["module"]);
    if (!parsed.success) {
      throw makeLoadError(moduleId, version, cdnUrl, {
        code: "INVALID_MODULE",
        reason:
          'Bundle does not export a valid ModuleDefinition as "module". ' +
          `Exports found: [${Object.keys(namespace).join(", ")}]. ` +
          `Validation: ${parsed.error.message}`,
      });
    }

    if (parsed.data.id !== moduleId) {
      throw makeLoadError(moduleId, version, cdnUrl, {
        code: "INVALID_MODULE",
        reason: `Module ID mismatch: requested "${moduleId}" but bundle exports "${parsed.data.id}"`,
      });
    }

    if (parsed.data.type === "error" && !options?.allowErrorModule) {
      throw makeLoadError(moduleId, version, cdnUrl, {
        code: "INVALID_MODULE",
        reason: `Module "${moduleId}" has type "error" and cannot be loaded through ModuleRenderer. Use ShellErrorBoundary instead.`,
      });
    }

    return {
      definition: parsed.data as LoadedModule["definition"],
      cdnUrl,
    };
  }

  async function preload(
    modules: Array<{ moduleId: ModuleID; version: VersionString }>,
  ): Promise<Array<LoadModuleResult>> {
    const results = await Promise.allSettled(
      modules.map(({ moduleId, version }) =>
        loadModule(moduleId, version, { cacheMode: "preload" }),
      ),
    );

    return results.map((result) => {
      if (result.status === "fulfilled") {
        return { ok: true as const, module: result.value };
      }
      return { ok: false as const, error: result.reason as ModuleLoadError };
    });
  }

  return { loadModule, preload };
}
